import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, CreditCard, Clock, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SubscriptionGuard - Enforces subscription status for clinic users
 * 
 * - Shows warning banners during trial period
 * - Shows urgent banners during grace period  
 * - Blocks access completely when trial + grace period expires
 */
export default function SubscriptionGuard({ children }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  const checkSubscriptionStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      let clinicId = null;
      
      // Check if impersonating - get clinic_id from impersonated user or stored clinic_id
      const impersonationData = localStorage.getItem('impersonation_data');
      if (impersonationData) {
        try {
          const impData = JSON.parse(impersonationData);
          // Get clinic_id from impersonated user data
          clinicId = impData.impersonated_user?.clinic_id;
        } catch (e) {
          console.error('Error parsing impersonation data:', e);
        }
      }
      
      // Also check localStorage for clinic_id (set during impersonation)
      if (!clinicId) {
        clinicId = localStorage.getItem('clinic_id');
      }
      
      if (!token) {
        setLoading(false);
        return;
      }

      // Build URL with clinic_id if available (for impersonation scenarios)
      let url = `${API}/api/trial-management/status`;
      if (clinicId) {
        url += `?clinic_id=${clinicId}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to check subscription status:', error);
      // On error, allow access (fail open for better UX)
      setStatus({ can_access: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSubscriptionStatus();
    
    // Re-check every 5 minutes
    const interval = setInterval(checkSubscriptionStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [checkSubscriptionStatus]);

  // Handle payment redirect
  const handleSubscribe = () => {
    navigate('/admin/subscription');
  };

  // Loading state
  if (loading) {
    return children;
  }

  // No status or can access - show children
  if (!status || status.can_access) {
    // Show warning banner if applicable
    if (status?.warning && !dismissed) {
      return (
        <>
          <WarningBanner 
            status={status} 
            onDismiss={() => setDismissed(true)}
            onSubscribe={handleSubscribe}
          />
          {children}
        </>
      );
    }
    return children;
  }

  // BLOCKED - Show full screen modal
  if (status.is_blocked) {
    return (
      <BlockedModal 
        status={status} 
        onSubscribe={handleSubscribe}
      />
    );
  }

  return children;
}


/**
 * Warning Banner - Shown during trial period when expiration is approaching
 */
function WarningBanner({ status, onDismiss, onSubscribe }) {
  const isGracePeriod = status.status === 'grace_period';
  const isUrgent = status.show_urgent_banner || isGracePeriod;
  
  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 px-4 py-3 flex items-center justify-between ${
        isUrgent 
          ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white' 
          : 'bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900'
      }`}
      data-testid="subscription-warning-banner"
    >
      <div className="flex items-center gap-3">
        {isUrgent ? (
          <AlertTriangle className="h-5 w-5 animate-pulse" />
        ) : (
          <Clock className="h-5 w-5" />
        )}
        <span className="font-medium">
          {status.warning}
        </span>
      </div>
      
      <div className="flex items-center gap-3">
        <Button 
          onClick={onSubscribe}
          size="sm"
          className={isUrgent 
            ? 'bg-white text-red-600 hover:bg-gray-100' 
            : 'bg-gray-900 text-white hover:bg-gray-800'
          }
          data-testid="subscribe-now-btn"
        >
          <CreditCard className="h-4 w-4 mr-2" />
          Subscribe Now
        </Button>
        
        {!isUrgent && (
          <button 
            onClick={onDismiss}
            className="text-gray-700 hover:text-gray-900"
            aria-label="Dismiss"
          >
            <XCircle className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * Blocked Modal - Full screen blocker when subscription has expired
 */
function BlockedModal({ status, onSubscribe }) {
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('clinic_id');
    window.location.href = '/login';
  };

  return (
    <div 
      className="fixed inset-0 z-[100] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4"
      data-testid="subscription-blocked-modal"
    >
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 px-8 py-10 text-center text-white">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-10 w-10" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Trial Period Expired</h1>
          <p className="text-red-100">Your free trial and grace period have ended</p>
        </div>

        {/* Content */}
        <div className="px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-center">
              {status.message || 'Please subscribe to continue using the platform.'}
            </p>
          </div>

          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-teal-600 font-bold">1</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Choose a subscription plan</p>
                <p className="text-sm text-gray-600">Select the plan that fits your clinic's needs</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-teal-600 font-bold">2</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Complete payment</p>
                <p className="text-sm text-gray-600">Secure checkout powered by Stripe</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-teal-600 font-bold">3</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Get instant access</p>
                <p className="text-sm text-gray-600">All your data is preserved and ready to use</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={onSubscribe}
              className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white py-6 text-lg"
              data-testid="blocked-subscribe-btn"
            >
              <CreditCard className="h-5 w-5 mr-2" />
              View Subscription Plans
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            
            <Button 
              onClick={handleLogout}
              variant="ghost"
              className="w-full text-gray-500 hover:text-gray-700"
              data-testid="blocked-logout-btn"
            >
              Sign Out
            </Button>
          </div>

          <p className="text-center text-xs text-gray-500 mt-6">
            Need help? Contact us at{' '}
            <a href="mailto:support@mymedmember.com" className="text-teal-600 hover:underline">
              support@mymedmember.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}


/**
 * Hook to get subscription status
 */
export function useSubscriptionStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API}/api/trial-management/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setStatus(response.data);
      } catch (error) {
        console.error('Failed to check subscription status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, []);

  return { status, loading };
}
